import type { GuardDeclaration } from '@/utils/server/redirect-utils';
import { getMarketingRegionIfEnabled } from '@/utils/server/region-utils';

export type RelevantPath = {
    path: string;
    exact?: boolean;
    /**
     * When `true`, the path counts as a flow-relevant URL only while the user is already in that
     * flow (sessionStorage `FLOW_ID_SESSION_KEY` already holds the flow id). Used by `decideFlowId`
     * and `useFlowRouteGuard` to gate pages that should never be entered cold. See
     * `.ai-rules/06-shared-libraries/tn-flows.md` (Route guards).
     */
    protected?: boolean;
    /**
     * SSR guard declarations evaluated by `evaluateFlowGuardsSSR` (`@tn/flows/route-guards`)
     * when this path matches the requested pathname. See
     * `.ai-rules/06-shared-libraries/tn-flows.md` (Route guards).
     */
    guards?: readonly GuardDeclaration[];
};

const getRegionSegment = (
    pathname: string,
    region: string,
    allowMarketingRegionInPath: boolean,
): string | undefined => {
    const firstSegment = pathname.split('/').filter((segment) => segment !== '')?.[0];
    if (!firstSegment) {
        return undefined;
    }
    if (getMarketingRegionIfEnabled(allowMarketingRegionInPath, region) === firstSegment) {
        return firstSegment;
    }
};

export const matchRelevantPath = (
    pathname: string,
    relevantPath: RelevantPath,
    region: string,
    getConfigByKey: (key: string) => unknown,
): boolean => {
    const allowMarketingRegionInPath = getConfigByKey(
        'options.allowMarketingRegionInPath',
    ) as boolean;
    const regionSegment = getRegionSegment(pathname, region, allowMarketingRegionInPath);

    let adjustedPath = relevantPath.path;
    if (
        regionSegment &&
        pathname.startsWith(`/${regionSegment}`) &&
        !adjustedPath.startsWith(`/${regionSegment}`)
    ) {
        adjustedPath = `/${regionSegment}${relevantPath.path}`;
    }
    if (relevantPath.exact) {
        return pathname === adjustedPath;
    }
    return pathname.startsWith(adjustedPath);
};
